import { type ScoringProfile, type ScoringType } from "./types";
import type { ScoringModel } from "@/apis/argo/scoringModels";
import {
  FIT_CRITERION_KEYS,
  INTENT_CRITERION_KEYS,
  getCriterionKey,
  CRITERION_REGISTRY,
} from "./criterionRegistry";
import {
  type OpportunityGeneratorScoringProfileSalesplaySearch,
  type ScoringProfile as BackendScoringCriterion,
  type ModelScoreInput,
  ModelScoreColumn,
} from "@/generated/company-search";

function isFitCriterion(c: ScoringType): boolean {
  const key = getCriterionKey(c);
  return key !== null && (FIT_CRITERION_KEYS as string[]).includes(key);
}

function isIntentCriterion(c: ScoringType): boolean {
  const key = getCriterionKey(c);
  return key !== null && (INTENT_CRITERION_KEYS as string[]).includes(key);
}

/** Whether a profile scores on the fit and/or intent axes — the Fit×Intent
 * matrix requires both. */
export function profileScoringAxes(profile: ScoringProfile): {
  hasFit: boolean;
  hasIntent: boolean;
} {
  const { criteria } = profile.parameters;
  return {
    hasFit: criteria.some(isFitCriterion),
    hasIntent: criteria.some(isIntentCriterion),
  };
}

// Transforms Niffler profiles into the shape expected by opportunityGeneratorSearch
export function transformFilterScoring(
  activeProfiles: ScoringProfile[]
): OpportunityGeneratorScoringProfileSalesplaySearch[] {
  return activeProfiles.flatMap((profile, index) => {
    const { criteria: rawCriteria } = profile.parameters;

    if (rawCriteria.length === 0) return [];

    const criteria = rawCriteria.map((c) => {
      const key = getCriterionKey(c);
      return key ? (CRITERION_REGISTRY[key]?.sanitize?.(c) ?? c) : c;
    });

    const fitNames = criteria.filter(isFitCriterion).map((c) => c.name);
    const intentNames = criteria.filter(isIntentCriterion).map((c) => c.name);

    return [
      {
        scoringProfileId: profile.id,
        order: index + 1,
        scoringCriteria: [
          ...(criteria as BackendScoringCriterion[]),
          ...(fitNames.length > 0
            ? [{ name: "Fit Score", sum: fitNames }]
            : []),
          ...(intentNames.length > 0
            ? [{ name: "Intent Score", sum: intentNames }]
            : []),
        ],
        localizeInstallsForProfile: profile.parameters.localizeInstalls ?? true,
      },
    ];
  });
}

/**
 * Strips criteria with no valid band data before sending to backend.
 * A criterion is invalid if all its array fields are empty.
 */
export function sanitizeScoringCriteriaForBackend(
  profiles: ScoringProfile[]
): ScoringProfile[] {
  return profiles
    .map((profile) => ({
      ...profile,
      parameters: {
        ...profile.parameters,
        criteria: profile.parameters.criteria.filter((criterion) => {
          return Object.entries(criterion).some(
            ([k, v]) =>
              k !== "name" &&
              k !== "weight" &&
              k !== "wasGenerated" &&
              Array.isArray(v) &&
              v.length > 0
          );
        }),
      },
    }))
    .filter((profile) => profile.parameters.criteria.length > 0);
}

/** Returns the full per-criterion scores record for a given profile. */
export function getRawProfileScores(
  scores: unknown,
  profileId: number
): Record<string, number> {
  if (!Array.isArray(scores)) return {};
  const entry = (
    scores as Array<{
      scoringProfileId: number;
      scores: Record<string, number>;
    }>
  ).find((s) => s.scoringProfileId === profileId);
  return entry?.scores ?? {};
}

// scores comes back as an array: [{ scoringProfileId, order, scores: Record<string, number> }]
export function getProfileScores(
  scores: unknown,
  profileId: number
): {
  fitScoreNormalized: number | null;
  intentScoreNormalized: number | null;
} {
  if (!Array.isArray(scores))
    return { fitScoreNormalized: null, intentScoreNormalized: null };
  const entry = (
    scores as Array<{
      scoringProfileId: number;
      scores: Record<string, number>;
    }>
  ).find((s) => s.scoringProfileId === profileId);
  return {
    fitScoreNormalized: entry?.scores?.["Fit Score Normalized"] ?? null,
    intentScoreNormalized: entry?.scores?.["Intent Score Normalized"] ?? null,
  };
}

// ── V2 (argo model) scoring ────────────────────────────────────────────────
// The V2 path is additive and independent of the V1 `scoringProfiles` flow
// above: a single opportunity_generator_search can carry both. V2 references a
// published argo model by its stable `family` id and lets hermes join the
// pre-computed Parquet score export, rather than sending inline criteria bands.

/**
 * All five score columns. `composite` + `tier` exist in every model's exported
 * Parquet; `fit`/`need`/`intent` only for the pillars a model scores. Hermes
 * (PR #1025) inspects the Parquet schema and projects an absent column as
 * NULL, so requesting all of them is safe — a pillar the model doesn't score
 * simply comes back null on every row.
 */
export const DEFAULT_MODEL_SCORE_COLUMNS: ModelScoreColumn[] = [
  ModelScoreColumn.COMPOSITE,
  ModelScoreColumn.TIER,
  ModelScoreColumn.FIT,
  ModelScoreColumn.NEED,
  ModelScoreColumn.INTENT,
];

/**
 * Builds the `model_scores` argument for opportunity_generator_search from the
 * active V2 models. `modelId` is the model's stable `family` id. Returns `[]`
 * (never null) when none are active — hermes types `model_scores` as
 * non-nullable and 422s the whole request on null.
 */
export function buildModelScores(
  activeModels: ScoringModel[],
  columns: ModelScoreColumn[] = DEFAULT_MODEL_SCORE_COLUMNS
): ModelScoreInput[] {
  return activeModels.map((model) => ({ modelId: model.family, columns }));
}

/** A per-row V2 score, as parsed out of `data[].model_scores[<family>]`.
 * `composite` is already a 0–100 percentage; `tier` is a threshold label. */
export interface ModelScoreResult {
  composite?: number;
  tier?: string;
  fit?: number;
  need?: number;
  intent?: number;
}

/**
 * Reads one model's scores out of a row's `model_scores` map (string-keyed by
 * `family`). Returns null when the row has no scores for that family.
 */
export function getModelScores(
  modelScores: unknown,
  family: string
): ModelScoreResult | null {
  if (!modelScores || typeof modelScores !== "object") return null;
  const entry = (modelScores as Record<string, unknown>)[family];
  if (!entry || typeof entry !== "object") return null;
  return entry as ModelScoreResult;
}
